/** Shared, optional footer enhancement. Native scrolling owns every frame. */
export function mountFooter(footer, { enabled = true } = {}) {
  if (!footer) return () => {};
  const main = document.getElementById('main-content');
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = matchMedia('(hover: hover) and (pointer: fine)');
  const sentinel = document.createElement('div');
  sentinel.className = 'kb-footer-sentinel';
  sentinel.setAttribute('aria-hidden', 'true');
  footer.before(sentinel);
  let disposed = false;
  // Keep keyboard targets still, including after blur. Pointer focus must not
  // cancel a running transform between down/up and move the clicked link.
  const quiet = event => {
    if (event.target.matches(':focus-visible')) footer.setAttribute('data-footer-quiet', '');
  };
  footer.addEventListener('focusin', quiet);
  const measure = () => {
    if (disposed) return;
    // Tall/touch/reading footers stay in flow so every link is reachable.
    const eligible = enabled && !motion.matches && pointer.matches && main &&
      footer.offsetHeight < innerHeight - 80 && main.offsetHeight > innerHeight + footer.offsetHeight / 2;
    footer.toggleAttribute('data-footer-reveal', !!eligible);
  };
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
  resize?.observe(footer);
  if (main) resize?.observe(main);
  const enter = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    if (entries.some(e => e.isIntersecting)) {
      footer.setAttribute('data-footer-entered', '');
      enter.disconnect();
    }
  }) : null;
  enter?.observe(sentinel);
  // Intersection alone is insufficient: the sticky footer intersects the
  // viewport while the opaque main page is still covering it. Start only once
  // the whole mark is in the safe viewport AND actually uncovered.
  const mark = footer.querySelector('[data-decorative]');
  if (enabled && mark) footer.setAttribute('data-footer-mark-ready', '');
  let markFrame = 0;
  let markPlayed = false;
  const checkMark = () => {
    markFrame = 0;
    if (disposed || markPlayed || !mark || !enabled || motion.matches) return;
    const r = mark.getBoundingClientRect();
    if (r.top < 80 || r.bottom > innerHeight - 24 || r.height === 0) return;
    if (footer.hasAttribute('data-footer-reveal') && main?.getBoundingClientRect().bottom > r.top) return;
    const uncovered = [0.2, 0.5, 0.8].every(x =>
      footer.contains(document.elementFromPoint(r.left + r.width * x, r.top + r.height / 2)));
    if (!uncovered) return;
    markPlayed = true;
    footer.setAttribute('data-footer-mark-entered', '');
    markEnter?.disconnect();
    window.removeEventListener('scroll', scheduleMark);
  };
  const scheduleMark = () => {
    if (!disposed && !markPlayed && !markFrame) markFrame = requestAnimationFrame(checkMark);
  };
  const markEnter = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    if (entries.some(e => e.isIntersecting)) scheduleMark();
  }, { threshold: [0, 1], rootMargin: '0px 0px -24px 0px' }) : null;
  if (mark) markEnter?.observe(mark);
  // Passive and frame-coalesced; removed permanently after the one-shot reveal.
  // No scroll interception, element resizing, or background animation loop.
  if (enabled && mark) window.addEventListener('scroll', scheduleMark, { passive: true });
  window.addEventListener('resize', scheduleMark);
  motion.addEventListener('change', scheduleMark);
  motion.addEventListener('change', measure);
  pointer.addEventListener('change', measure);
  window.addEventListener('resize', measure);
  document.fonts?.ready.then(measure);
  measure();
  scheduleMark();
  return () => {
    disposed = true;
    cancelAnimationFrame(markFrame);
    window.removeEventListener('scroll', scheduleMark);
    window.removeEventListener('resize', scheduleMark);
    motion.removeEventListener('change', scheduleMark);
    resize?.disconnect(); enter?.disconnect(); markEnter?.disconnect(); sentinel.remove();
    motion.removeEventListener('change', measure);
    pointer.removeEventListener('change', measure);
    window.removeEventListener('resize', measure);
    footer.removeEventListener('focusin', quiet);
    footer.removeAttribute('data-footer-reveal');
    footer.removeAttribute('data-footer-entered');
    footer.removeAttribute('data-footer-mark-entered');
    footer.removeAttribute('data-footer-mark-ready');
    footer.removeAttribute('data-footer-quiet');
  };
}

// The static shell loads this same module, not a second implementation.
if (typeof document !== 'undefined' && document.body?.classList.contains('kb-body')) {
  mountFooter(document.querySelector('[data-testid="site-footer"]'), {
    enabled: document.body.classList.contains('kb-surface-marketing'),
  });
}
